import { onBeforeUnmount, onMounted, ref } from 'vue'

const MICROPHONE_STORAGE_KEY = 'goodscriber.microphoneDeviceId'
const DEFAULT_AUDIO_CONSTRAINTS = Object.freeze({
  channelCount: 1,
  echoCancellation: true,
  autoGainControl: true,
  noiseSuppression: true,
})

const HIGH_QUALITY_AUDIO_CONSTRAINTS = Object.freeze({
  channelCount: { ideal: 1 },
  sampleRate: { ideal: 48000 },
  echoCancellation: true,
  autoGainControl: true,
  noiseSuppression: true,
})

function readStoredDeviceId() {
  try {
    return window.localStorage.getItem(MICROPHONE_STORAGE_KEY) || ''
  } catch {
    return ''
  }
}

function storeDeviceId(deviceId) {
  try {
    if (deviceId) {
      window.localStorage.setItem(MICROPHONE_STORAGE_KEY, deviceId)
    } else {
      window.localStorage.removeItem(MICROPHONE_STORAGE_KEY)
    }
  } catch {}
}

function buildAudioConstraints(deviceId = '', baseConstraints = DEFAULT_AUDIO_CONSTRAINTS) {
  return {
    ...baseConstraints,
    ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
  }
}

function isUnavailableDeviceError(error) {
  return error?.name === 'NotFoundError' || error?.name === 'OverconstrainedError'
}

export function useMicrophoneDevices() {
  const microphoneDevices = ref([])
  const selectedMicrophoneId = ref(readStoredDeviceId())
  const isLoadingMicrophones = ref(false)

  function ensureMediaDevices() {
    if (!navigator.mediaDevices?.getUserMedia || !navigator.mediaDevices?.enumerateDevices) {
      throw new Error('Audio input selection is not supported in this browser')
    }
  }

  function selectMicrophone(deviceId) {
    const normalizedDeviceId = String(deviceId || '')
    selectedMicrophoneId.value = normalizedDeviceId
    storeDeviceId(normalizedDeviceId)
  }

  async function acquireMicrophoneStream() {
    ensureMediaDevices()
    try {
      return await navigator.mediaDevices.getUserMedia({
        audio: buildAudioConstraints(selectedMicrophoneId.value),
      })
    } catch (error) {
      if (!selectedMicrophoneId.value || !isUnavailableDeviceError(error)) throw error

      // A previously selected microphone may have been unplugged or renamed.
      // Fall back to the operating system default so recording can still start.
      selectMicrophone('')
      return navigator.mediaDevices.getUserMedia({
        audio: buildAudioConstraints(),
      })
    }
  }

  async function acquireHighQualityMicrophoneStream() {
    ensureMediaDevices()
    try {
      return await navigator.mediaDevices.getUserMedia({
        audio: buildAudioConstraints(selectedMicrophoneId.value, HIGH_QUALITY_AUDIO_CONSTRAINTS),
      })
    } catch (error) {
      if (!selectedMicrophoneId.value || !isUnavailableDeviceError(error)) throw error
      selectMicrophone('')
      return navigator.mediaDevices.getUserMedia({
        audio: buildAudioConstraints('', HIGH_QUALITY_AUDIO_CONSTRAINTS),
      })
    }
  }

  // 跟读期间只申请一次麦克风：说话检测和原声录音共用这一路流，暂停时不关闭。
  // 每次重新 getUserMedia，手机 Chrome 在未永久授权时会反复弹权限框。
  let sharedStream = null

  async function getSharedStream() {
    if (sharedStream?.active) return sharedStream
    sharedStream = await acquireHighQualityMicrophoneStream()
    return sharedStream
  }

  function releaseSharedStream() {
    sharedStream?.getTracks().forEach((track) => track.stop())
    sharedStream = null
  }

  async function refreshMicrophones({ requestPermission = false, validateSelection = false } = {}) {
    ensureMediaDevices()
    isLoadingMicrophones.value = true
    let permissionStream = null

    try {
      if (requestPermission) {
        permissionStream = await acquireMicrophoneStream()
      }

      const devices = await navigator.mediaDevices.enumerateDevices()
      microphoneDevices.value = devices
        .filter((device) => (
          device.kind === 'audioinput' &&
          device.deviceId &&
          device.deviceId !== 'default' &&
          device.deviceId !== 'communications'
        ))
        .map((device) => ({
          deviceId: device.deviceId,
          label: device.label,
        }))

      if (
        selectedMicrophoneId.value &&
        (requestPermission || validateSelection || microphoneDevices.value.length > 0) &&
        !microphoneDevices.value.some((device) => device.deviceId === selectedMicrophoneId.value)
      ) {
        selectMicrophone('')
      }

      return microphoneDevices.value
    } finally {
      permissionStream?.getTracks().forEach((track) => track.stop())
      isLoadingMicrophones.value = false
    }
  }

  function handleDeviceChange() {
    void refreshMicrophones({ validateSelection: true }).catch(() => {})
  }

  onMounted(() => {
    navigator.mediaDevices?.addEventListener?.('devicechange', handleDeviceChange)
    void refreshMicrophones().catch(() => {})
  })

  onBeforeUnmount(() => {
    navigator.mediaDevices?.removeEventListener?.('devicechange', handleDeviceChange)
    releaseSharedStream()
  })

  return {
    microphoneDevices,
    selectedMicrophoneId,
    isLoadingMicrophones,
    getSharedStream,
    releaseSharedStream,
    refreshMicrophones,
    selectMicrophone,
  }
}
